import { Injectable, InjectionToken } from '@angular/core';
import { HttpService } from './http.service';
import { EventService } from './event.service';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { finalize, tap, catchError, shareReplay, map, retry, delay } from 'rxjs/operators';
import { Hotel, HotelDescriptionData, CreateHotelRequest, CreateHotelInformationRequest, CompletenessData, AdminCountry, AllotmentStats, HotelsOverviewResponse } from '../interfaces/hotel';
import { BookingResponse, PaginatedBookingResponse } from '../interfaces/booking-response';
import { Booking } from '../interfaces/booking';
import { HotelAnalytics, AnalyticsQueryParams } from '../interfaces/hotel-analytics';
import { switchMap } from 'rxjs/operators';
import { ApiResponse, PaginatedResponse } from '../interfaces/api-response';
import { LoggerService } from './logger.service';

export const HOTEL_SERVICE_TOKEN = new InjectionToken<HotelService>('HotelService');

@Injectable({
  providedIn: 'root'
})
export class HotelService {
  private _hotelSubject = new BehaviorSubject<Hotel | null>(null);
  readonly selectedHotel$ = this._hotelSubject.asObservable();

  private hotelCache = new Map<string, Observable<Hotel | null>>();

  constructor(
    private http: HttpService,
    private eventService: EventService,
    private logger: LoggerService,
  ) {
    this.restoreLastHotel();
  }

  /**
   * Restores the last selected hotel from localStorage.
   * Used for pages that don't have hotel context in the URL (e.g., profile page).
   *
   * Guard: only runs when the user has a valid token. Without a token the API
   * call would return 401, which triggers the error interceptor and disrupts
   * the login flow (e.g. clearing auth state while the user is mid-login).
   */
  private restoreLastHotel(): void {
    const storedHotelId = localStorage.getItem('lastHotelId');
    const token = localStorage.getItem('token');

    if (storedHotelId && token && !this._hotelSubject.value) {
      this.initializeSelectedHotel(storedHotelId).subscribe({
        error: (err) => this.logger.warn('Failed to restore last hotel from localStorage:', err)
      });
    }
  }

  public initializeSelectedHotel(hotelId: string): Observable<Hotel | null> {
    const currentHotelId = this._hotelSubject.value?.id?.toString();
    if (currentHotelId === hotelId) {
      return of(this._hotelSubject.value);
    }

    if (this.hotelCache.has(hotelId)) {
      return this.hotelCache.get(hotelId)!;
    }

    this.eventService.broadcast('globalLoader:show', true);

    const request$ = this.getHotel(hotelId).pipe(
      map((resp: ApiResponse<Hotel>) => {
        const hotel = resp?.data ?? null;
        if (hotel) {
            const hotelWithSettings = hotel as Hotel & { settings?: { completeness_percentage?: number } };
          if (hotelWithSettings.settings?.completeness_percentage !== undefined && !hotel.completeness_percentage) {
            hotel.completeness_percentage = hotelWithSettings.settings.completeness_percentage;
          }
        }
        return hotel;
      }),
      tap(hotel => {
        if (!hotel) throw new Error(`No hotel received for ID ${hotelId}`);
        this._hotelSubject.next(hotel);
        this.eventService.changeManagedHotel(hotel);
        localStorage.setItem('lastHotelId', hotel.id.toString());
      }),
      catchError(err => {
        this.logger.error(`Failed to initialize hotel ${hotelId}`, err);
        this.clearSelectedHotel();
        this.hotelCache.delete(hotelId);
        return throwError(() => err);
      }),
      finalize(() => {
        this.eventService.broadcast('globalLoader:hide', false);
      }),
      shareReplay(1)
    );

    this.hotelCache.set(hotelId, request$);
    return request$;
  }

  getHotels(filters: Record<string, string | number | boolean>): Observable<PaginatedResponse<Hotel>> {
    return this.http.get<PaginatedResponse<Hotel>>('v2/hotels', { params: filters as Record<string, string> });
  }

  /** Super-Admin-only "All Hotels" overview table (GET extranet/hotels). */
  getAllHotelsOverview(filters: Record<string, string | number>): Observable<HotelsOverviewResponse> {
    return this.http.get<HotelsOverviewResponse>('v2/extranet/hotels', { params: filters as Record<string, string> });
  }

  getHotel(id: string | number): Observable<ApiResponse<Hotel>> {
    return this.http.get<ApiResponse<Hotel>>(`v2/hotels/information/${id}`);
  }

  getAllotmentStats(hotelId: number | string): Observable<ApiResponse<AllotmentStats>> {
    return this.http.get<ApiResponse<AllotmentStats>>(`v2/hotels/${hotelId}/dashboard/allotment-stats`);
  }

  getHotelInfo(): Observable<ApiResponse<Hotel> | null> {
    return this.selectedHotel$.pipe(
      switchMap(hotel => hotel ? this.http.get<ApiResponse<Hotel>>(`v2/hotels/information/${hotel.id}`) : of(null))
    );
  }

  getSelectedHotel(): Observable<Hotel | null> {
    return this.selectedHotel$;
  }

  get hotelId(): string | null {
    return this._hotelSubject.value?.id?.toString() ?? null;
  }

  get selectedHotelSnapshot(): Hotel | null {
    return this._hotelSubject.value;
  }

  /**
   * Re-fetches the currently selected hotel from the API and pushes the
   * fresh data to selectedHotel$. Call this after saving hotel information
   * so all subscribers (e.g. room currency display) receive the updated values
   * without requiring a full page refresh.
   */
  refreshSelectedHotel(): Observable<Hotel | null> {
    const hotel = this._hotelSubject.value;
    if (!hotel) return of(null);

    const hotelId = hotel.id.toString();
    this.hotelCache.delete(hotelId);

    return this.getHotel(hotelId).pipe(
      map((resp: ApiResponse<Hotel>) => resp?.data ?? null),
      tap(fresh => {
        if (fresh) {
          this._hotelSubject.next(fresh);
          this.eventService.changeManagedHotel(fresh);
        }
      }),
      catchError(err => {
        this.logger.error('Failed to refresh selected hotel', err);
        return of(null);
      })
    );
  }

  saveHotel(id: string | number, data: Partial<Hotel>): Observable<ApiResponse<Hotel>> {
    return this.http.put<ApiResponse<Hotel>>(`v2/hotels/${id}`, data);
  }

  /**
   * Immediately merges partial hotel data into the current selectedHotel$ value.
   * Use this after a successful save to propagate field changes (e.g. currency)
   * to all subscribers before the async refreshSelectedHotel() call completes.
   */
  updateSelectedHotel(partial: Partial<Hotel>): void {
    const current = this._hotelSubject.value;
    if (current) {
      this._hotelSubject.next({ ...current, ...partial });
    }
  }

  getBookings(id: string | number, params: Record<string, string | number>): Observable<BookingResponse> {
    return this.http.get<BookingResponse>(`v2/hotels/${id}/bookings/all`, { params });
  }

  getSelectedHotelBookings(params: Record<string, string | number>): Observable<BookingResponse | null> {
    const id = this.hotelId;
    return id ? this.getBookings(id, params) : of(null);
  }

  getPaginatedBookings(params: Record<string, string | number>): Observable<PaginatedBookingResponse | null> {
    const id = this.hotelId;
    if (!id) return of(null);

    return this.http.get<{ status: string; data: { bookings: Booking[]; total: number } }>(`v2/hotels/${id}/bookings/all`, { params }).pipe(
      retry({
        count: 3,
        delay: (error, retryCount) => {
          this.logger.warn(`HotelService bookings API retry attempt ${retryCount}:`, error);
          return of(null).pipe(delay(500 * retryCount));
        }
      }),
      map((response) => {
        if (!response || !response.data) return null;

        const page = Number(params['page']) || 1;
        const perPage = Number(params['per_page']) || 10;
        const total = response.data.total || 0;
        const bookings = response.data.bookings || [];

        const from = total > 0 ? (page - 1) * perPage + 1 : 0;
        const to = Math.min(page * perPage, total);

        return {
          data: bookings,
          total: total,
          from: from,
          to: to,
          current_page: page,
          per_page: String(perPage),
          last_page: Math.ceil(total / perPage)
        } as PaginatedBookingResponse;
      }),
      catchError((error) => {
        this.logger.error('HotelService: Failed to load bookings after retries:', error);
        return of(null);
      })
    );
  }

  saveHotelinfo(data: Partial<Hotel>): Observable<ApiResponse<Hotel> | null> {
    const id = this.hotelId;
    return id ? this.http.put<ApiResponse<Hotel>>(`v2/hotels/information/${id}`, data) : of(null);
  }

  getDescription(): Observable<ApiResponse<HotelDescriptionData> | null> {
    const id = this.hotelId;
    return id ? this.http.get<ApiResponse<HotelDescriptionData>>(`v2/hotels/description/${id}`) : of(null);
  }

  saveDescription(payload: HotelDescriptionData): Observable<ApiResponse<HotelDescriptionData> | null> {
    const id = this.hotelId;
    return id ? this.http.put<ApiResponse<HotelDescriptionData>>(`v2/hotels/description/${id}`, payload) : of(null);
  }

  clearSelectedHotel(): void {
    this._hotelSubject.next(null);
    this.eventService.broadcast('hotel:cleared', null);
  }

  clearHotelCache(): void {
    this.hotelCache.clear();
  }

  isHotelCached(hotelId: string): boolean {
    return this.hotelCache.has(hotelId);
  }

  getAnalytics(hotelId: string | number, params?: AnalyticsQueryParams): Observable<ApiResponse<HotelAnalytics>> {
    return this.http.get<ApiResponse<HotelAnalytics>>(`v2/hotels/analytics/${hotelId}`, {
      params: params as Record<string, string> | undefined
    });
  }

  getSelectedHotelAnalytics(params?: AnalyticsQueryParams): Observable<ApiResponse<HotelAnalytics> | null> {
    const id = this.hotelId;
    return id ? this.getAnalytics(id, params) : of(null);
  }

  getPendingBookingsCount(hotelId: string | number): Observable<ApiResponse<{ count: number }>> {
    return this.http.get<ApiResponse<{ count: number }>>(`v2/hotels/pending-count/${hotelId}`);
  }

  getSelectedHotelPendingCount(): Observable<ApiResponse<{ count: number }> | null> {
    const id = this.hotelId;
    return id ? this.getPendingBookingsCount(id) : of(null);
  }

  getAllPendingBookingsCounts(): Observable<ApiResponse<{ counts: Record<number, number> }>> {
    return this.http.get<ApiResponse<{ counts: Record<number, number> }>>('v2/hotels/pending-counts');
  }

  createHotel(data: CreateHotelRequest): Observable<{ message: string; hotelId: number | null }> {
    return this.http.post<ApiResponse<{ id?: number; hotel?: Hotel }>>('v2/extranet/hotels/register', data).pipe(
      map(response => ({
        message: response.message ?? '',
        hotelId: response.data?.id ?? response.data?.hotel?.id ?? null
      }))
    );
  }

  /**
   * Creates a new hotel by posting identity and configuration data.
   * Used as the first step in hotel creation — returns the created hotel with its ID.
   */
  createHotelInformation(data: CreateHotelInformationRequest): Observable<Hotel> {
    return this.http.post<ApiResponse<Hotel>>('v2/hotels/information/', data).pipe(
      map(response => response.data!)
    );
  }

  getCompletenessBreakdown(hotelId: number): Observable<CompletenessData> {
    return this.http.get<ApiResponse<CompletenessData>>(`v2/hotels/completeness/${hotelId}`).pipe(
      map(response => response.data!)
    );
  }

  /**
   * Fetches the target countries for admin hotel creation.
   * Always returns CH, DE, FR, AT, IT regardless of published hotels.
   */
  getAdminCountries(): Observable<ApiResponse<AdminCountry[]>> {
    return this.http.get<ApiResponse<AdminCountry[]>>('v2/extranet/countries/admin');
  }
}
